import { Progress } from '@/generic/progress/domain/Progress.ts';
import {
  IpponButton,
  IpponContainer,
  IpponErrorArea,
  IpponHSpace,
  IpponHSpaceSlot,
  IpponIconTile,
  IpponProgress,
  IpponTitle,
  IpponText,
  IpponVSpace,
} from '@ippon-ui/react';
import { Optional } from '@/generic/functional';
import { useDialog, useOptional } from '@/generic/react-tools';

export type LoadingFailure = {
  error: Error;
  title: string;
  description: string;
  detailLabel: string;
  retryLabel: string;
  closeLabel: string;
  onRetry: () => void;
};

const LoadingModalProgressContent = ({
  progress,
  title,
  dataSelector,
}: {
  progress: Optional<Progress>;
  title: string;
  dataSelector?: string;
}) => {
  const current = progress.orElse(Progress.start());

  return (
    <IpponVSpace gap={16}>
      <IpponProgress value={current.value} min={0} max={100} label={title} />
      <IpponText
        variant="body"
        dataSelector={dataSelector ? `${dataSelector}.progress` : undefined}
      >
        {current.readable}
      </IpponText>
    </IpponVSpace>
  );
};

const LoadingModalWaitingContent = ({ dataSelector }: { dataSelector?: string }) => (
  <div aria-busy="true">
    <IpponText
      variant="body"
      placeholder
      dataSelector={dataSelector ? `${dataSelector}.waiting` : undefined}
    />
  </div>
);

const LoadingModalBodyContent = ({
  failure,
  progress,
  title,
  dataSelector,
}: {
  failure: Optional<LoadingFailure>;
  progress?: Optional<Progress>;
  title: string;
  dataSelector?: string;
}) =>
  useOptional({
    optional: failure,
    onValuated: (f) => (
      <IpponContainer>
        <IpponErrorArea
          title={f.title}
          description={f.description}
          detailLabel={f.detailLabel}
          detailMessage={f.error}
          dataSelector={dataSelector ? `${dataSelector}.error` : undefined}
        />
      </IpponContainer>
    ),
    onEmpty: () =>
      progress === undefined ? (
        <LoadingModalWaitingContent dataSelector={dataSelector} />
      ) : (
        <LoadingModalProgressContent
          progress={progress}
          title={title}
          dataSelector={dataSelector}
        />
      ),
  });

const LoadingModalCloseButton = ({
  failure,
  onClose,
  dataSelector,
}: {
  failure: LoadingFailure;
  onClose?: () => void;
  dataSelector?: string;
}) =>
  useOptional({
    optional: Optional.ofUndefinable(onClose),
    onValuated: (close) => (
      <IpponButton
        color="neutral"
        variant="outline"
        size="small"
        onClick={close}
        dataSelector={dataSelector ? `${dataSelector}.close` : undefined}
      >
        {failure.closeLabel}
      </IpponButton>
    ),
  });

const LoadingModalCancelButton = ({
  cancelLabel,
  onClose,
  dataSelector,
}: {
  cancelLabel: string;
  onClose: () => void;
  dataSelector?: string;
}) => (
  <IpponButton
    color="neutral"
    variant="outline"
    size="small"
    onClick={onClose}
    dataSelector={dataSelector ? `${dataSelector}.cancel` : undefined}
  >
    {cancelLabel}
  </IpponButton>
);

const LoadingModalContinueButton = ({
  label,
  onContinueInBackground,
  dataSelector,
}: {
  label: string;
  onContinueInBackground: () => void;
  dataSelector?: string;
}) => (
  <IpponButton
    color="neutral"
    variant="outline"
    size="small"
    onClick={onContinueInBackground}
    dataSelector={dataSelector ? `${dataSelector}.continue` : undefined}
  >
    {label}
  </IpponButton>
);

const LoadingModalFooter = ({
  failure,
  cancelLabel,
  onClose,
  continueInBackground,
  dataSelector,
}: {
  failure: Optional<LoadingFailure>;
  cancelLabel?: string;
  onClose?: () => void;
  continueInBackground?: { label: string; onContinueInBackground: () => void };
  dataSelector?: string;
}) =>
  useOptional({
    optional: failure,
    onValuated: (f) => (
      <div className="ippon-modal--footer">
        <IpponHSpace gap={16} align="middle">
          <IpponButton
            size="small"
            onClick={f.onRetry}
            iconRight={{ name: 'refresh', variant: 'outline' }}
            dataSelector={dataSelector ? `${dataSelector}.retry` : undefined}
          >
            {f.retryLabel}
          </IpponButton>
          <LoadingModalCloseButton failure={f} onClose={onClose} dataSelector={dataSelector} />
        </IpponHSpace>
      </div>
    ),
    onEmpty: () =>
      (cancelLabel === undefined || onClose === undefined) && !continueInBackground ? undefined : (
        <div className="ippon-modal--footer">
          <IpponHSpace gap={16} align="middle">
            {cancelLabel !== undefined && onClose !== undefined ? (
              <LoadingModalCancelButton
                cancelLabel={cancelLabel}
                onClose={onClose}
                dataSelector={dataSelector}
              />
            ) : null}
            {continueInBackground ? (
              <LoadingModalContinueButton
                label={continueInBackground.label}
                onContinueInBackground={continueInBackground.onContinueInBackground}
                dataSelector={dataSelector}
              />
            ) : null}
          </IpponHSpace>
        </div>
      ),
  });

export const LoadingModalComponent = ({
  open,
  title,
  description,
  progress,
  failure = Optional.empty<LoadingFailure>(),
  cancelLabel,
  onClose,
  continueInBackgroundLabel,
  onContinueInBackground,
  dataSelector,
}: {
  open: boolean;
  title: string;
  description: string;
  progress?: Optional<Progress>;
  failure?: Optional<LoadingFailure>;
  cancelLabel?: string;
  onClose?: () => void;
  continueInBackgroundLabel?: string;
  onContinueInBackground?: () => void;
  dataSelector?: string;
}) => {
  const continueInBackground =
    continueInBackgroundLabel && onContinueInBackground
      ? { label: continueInBackgroundLabel, onContinueInBackground }
      : undefined;

  const dialog = useDialog(open, onClose);

  const headerTitle = failure.map((f) => f.title).orElse(title);
  const headerDescription = failure.map((f) => f.description).orElse(description);
  const headerIcon = failure.isPresent() ? 'alert-circle' : 'hourglass';
  const headerColor = failure.isPresent() ? 'error' : undefined;

  return (
    <dialog className="ippon-modal" ref={dialog} data-selector={dataSelector}>
      <div className="ippon-modal--header">
        <IpponHSpace gap={16} align="middle">
          <IpponIconTile
            icon={{ name: headerIcon, variant: 'outline' }}
            color={headerColor}
            size="small"
          />
          <IpponHSpaceSlot expand={true}>
            <IpponVSpace>
              <hgroup>
                <IpponTitle tag="h2" level={5}>
                  {headerTitle}
                </IpponTitle>
                <IpponText tag="p" variant="body" size="small" color="neutral-tertiary-inversed">
                  {headerDescription}
                </IpponText>
              </hgroup>
            </IpponVSpace>
          </IpponHSpaceSlot>
        </IpponHSpace>
      </div>
      <hr className="ippon-modal--separator" />
      <div className="ippon-modal--body">
        <LoadingModalBodyContent
          failure={failure}
          progress={progress}
          title={title}
          dataSelector={dataSelector}
        />
      </div>
      <LoadingModalFooter
        failure={failure}
        cancelLabel={cancelLabel}
        onClose={onClose}
        continueInBackground={continueInBackground}
        dataSelector={dataSelector}
      />
    </dialog>
  );
};
